import React, { cloneElement, useRef, Children, SyntheticEvent, ReactElement, ReactNode } from "react";
import { useId } from "@reach/auto-id";
import Box, { BoxProps } from "../box/Box";
import useDisclosure from "../utils/useDisclosure";
import Popper, { PopperArrow, PopperProps } from "../popper/Popper";
import VisuallyHidden from "../VisuallyHidden";

interface ITooltip {
  "aria-label"?: string;
  /**
   * The delay in `ms` for the tooltip to show
   */
  showDelay?: number;
  /**
   * The delay in `ms` for the tooltip to hide
   */
  hideDelay?: number;
  /**
   * The label of the tooltip.
   */
  label?: ReactNode;
  /**
   * The `ReactNode` to be used as the trigger of the tooltip.
   */
  children: ReactElement;
  /**
   * If `true` display an arrow tip on the tooltip.
   */
  hasArrow?: boolean;
  placement?: PopperProps["placement"];
  /**
   * If `true` hide the tooltip, when the trigger is clicked.
   */
  closeOnClick?: boolean;
  /**
   * If `true`, the tooltip is initially shown.
   */
  defaultIsOpen?: boolean;
  /**
   * If `true`, the tooltip is shown.
   */
  isOpen?: boolean;
  /**
   * If `true`, the tooltip will wrap the children in a `span` with `tabIndex=0`
   */
  shouldWrapChildren?: boolean;
  /**
   * Function called when the tooltip shows.
   */
  onOpen?: () => void;
  /**
   * Function called when the tooltip hides.
   */
  onClose?: () => void;
}

type TooltipProps = ITooltip & BoxProps;

const wrapEvent = (
  child: ReactElement,
  theirHandler: string,
  ourHandler: (event: React.SyntheticEvent<Element, Event>) => void,
) => (event: SyntheticEvent) => {
  if (typeof child !== "string" && child && child.props && child.props[theirHandler]) {
    child.props[theirHandler](event);
  }

  if (!event.defaultPrevented) {
    ourHandler(event);
  }
};

const Tooltip: React.FC<TooltipProps> = ({
  label,
  "aria-label": ariaLabel = label,
  showDelay = 0,
  hideDelay = 0,
  placement = "top",
  hasArrow = true,
  children,
  closeOnClick,
  defaultIsOpen,
  shouldWrapChildren,
  isOpen: controlledIsOpen,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  ...rest
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure(defaultIsOpen || false);
  const { current: isControlled } = useRef(controlledIsOpen != null);
  // eslint-disable-next-line
  const _isOpen = isControlled ? controlledIsOpen : isOpen;

  const referenceRef = useRef<any>();
  const enterTimeoutRef = useRef<any>();
  const exitTimeoutRef = useRef<any>();

  const openWithDelay = () => {
    enterTimeoutRef.current = setTimeout(onOpen, showDelay);
  };

  const closeWithDelay = () => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
    }
    exitTimeoutRef.current = setTimeout(onClose, hideDelay);
  };

  const tooltipId = `tooltip-${useId()}`;

  const handleOpen = () => {
    if (!isControlled) {
      openWithDelay();
    }

    if (onOpenProp) {
      onOpenProp();
    }
  };

  const handleClose = () => {
    if (!isControlled) {
      closeWithDelay();
    }

    if (onCloseProp) {
      onCloseProp();
    }
  };

  const handleClick = wrapEvent(children, "onClick", () => {
    if (closeOnClick) {
      closeWithDelay();
    }
  });

  const referenceProps = {
    ref: referenceRef,
    onMouseEnter: wrapEvent(children, "onMouseEnter", handleOpen),
    onMouseLeave: wrapEvent(children, "onMouseLeave", handleClose),
    onClick: handleClick,
    onFocus: wrapEvent(children, "onFocus", handleOpen),
    onBlur: wrapEvent(children, "onBlur", handleClose),
    ...(_isOpen && { "aria-describedby": tooltipId }),
  };

  let clone;

  if (typeof children === "string" || shouldWrapChildren) {
    clone = (
      <Box as="span" tabIndex={0} {...referenceProps}>
        {children}
      </Box>
    );
  } else {
    clone = cloneElement(Children.only(children as any), referenceProps);
  }

  const hasAriaLabel = ariaLabel != null;

  return (
    <>
      {clone}

      <Popper
        usePortal={true}
        isOpen={_isOpen}
        placement={placement}
        modifiers={{ offset: { enabled: true, offset: `0, 8` } }}
        anchorEl={referenceRef.current}
        arrowSize="10px"
        hasArrow={hasArrow}
        px="8px"
        py="2px"
        id={hasAriaLabel ? undefined : tooltipId}
        role={hasAriaLabel ? undefined : "tooltip"}
        bg="gray.700"
        borderRadius="sm"
        fontWeight="medium"
        pointerEvents="none"
        color="whiteAlpha.900"
        fontSize="sm"
        shadow="md"
        maxW="320px"
        zIndex={"tooltip" as any}
        {...rest}
      >
        {label}
        {hasAriaLabel && (
          <VisuallyHidden role="tooltip" id={tooltipId}>
            {ariaLabel}
          </VisuallyHidden>
        )}
        {hasArrow && <PopperArrow />}
      </Popper>
    </>
  );
};

export default Tooltip;
